// src/components/admin/SessaoForm.js
import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { createSessao, updateSessao } from '../../services/api';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Label } from '../ui/Label';
import { Textarea } from '../ui/Textarea';
import { DatePicker } from '../ui/DatePicker';
import { TimeInput } from '../ui/TimeInput';
import { format, parseISO, setHours, setMinutes } from 'date-fns';
import { cn } from '../../lib/utils'; // Importar cn para classes condicionais

function SessaoForm({ sessao, eventoId, onSuccess, onCancel }) {
    const [formData, setFormData] = useState({
        nome: '',
        descricao: '',
        dataInicio: undefined,
        horaInicio: '',
        dataFim: undefined,
        horaFim: '',
    });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [errors, setErrors] = useState({}); // Novo estado para erros de validação

    useEffect(() => {
        if (sessao) {
            const inicio = sessao.dataHoraInicio ? parseISO(sessao.dataHoraInicio) : undefined;
            const fim = sessao.dataHoraFim ? parseISO(sessao.dataHoraFim) : undefined;

            setFormData({
                nome: sessao.nome || '',
                descricao: sessao.descricao || '',
                dataInicio: inicio,
                horaInicio: inicio ? format(inicio, 'HH:mm') : '',
                dataFim: fim,
                horaFim: fim ? format(fim, 'HH:mm') : '',
            });
        } else {
            setFormData({
                nome: '',
                descricao: '',
                dataInicio: undefined,
                horaInicio: '',
                dataFim: undefined,
                horaFim: '',
            });
        }
        setErrors({}); // Limpa erros ao carregar/resetar
    }, [sessao]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
        // Limpa o erro ao digitar
        setErrors(prev => ({ ...prev, [name]: '' }));
    };

    const handleDateChange = (name, date) => {
        setFormData(prev => ({
            ...prev,
            [name]: date
        }));
        setErrors(prev => ({ ...prev, [name]: '' }));
    };

    const validateForm = () => {
        const newErrors = {};
        if (!formData.nome) newErrors.nome = 'O nome é obrigatório.';
        if (!formData.descricao) newErrors.descricao = 'A descrição é obrigatória.';
        if (!formData.dataInicio) newErrors.dataInicio = 'A data de início é obrigatória.';
        if (!formData.horaInicio) newErrors.horaInicio = 'A hora de início é obrigatória.';
        if (!formData.dataFim) newErrors.dataFim = 'A data de fim é obrigatória.';
        if (!formData.horaFim) newErrors.horaFim = 'A hora de fim é obrigatória.';

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validateForm()) {
            toast.error('Por favor, preencha todos os campos obrigatórios.');
            return;
        }

        setIsSubmitting(true);

        const combineDateTime = (dateObject, timeString) => {
            if (!dateObject || !timeString) return null;
            const [hours, minutes] = timeString.split(':').map(Number);
            let combined = setHours(dateObject, hours);
            combined = setMinutes(combined, minutes);
            return format(combined, "yyyy-MM-dd'T'HH:mm:ss");
        };

        const payload = {
            nome: formData.nome,
            descricao: formData.descricao,
            dataHoraInicio: combineDateTime(formData.dataInicio, formData.horaInicio),
            dataHoraFim: combineDateTime(formData.dataFim, formData.horaFim),
            eventoId: eventoId,
        };

        try {
            if (sessao) {
                await updateSessao(sessao.id, payload);
            } else {
                await createSessao(payload);
            }
            toast.success(`Sessão ${sessao ? 'atualizada' : 'criada'} com sucesso!`);
            onSuccess();
        } catch (err) {
            console.error("Erro no formulário da sessão:", err);
            toast.error(`Erro: ${err.message}`);
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid gap-2">
                <Label htmlFor="nome" className="mb-1">Nome da Sessão:</Label> {/* Adicionado mb-1 */}
                <Input
                    id="nome"
                    name="nome"
                    value={formData.nome}
                    onChange={handleChange}
                    required
                    className={cn(errors.nome && 'border-destructive focus-visible:ring-destructive')}
                />
                {errors.nome && <p className="text-xs text-destructive">{errors.nome}</p>}
            </div>
            <div className="grid gap-2">
                <Label htmlFor="descricao" className="mb-1">Descrição:</Label> {/* Adicionado mb-1 */}
                <Textarea
                    id="descricao"
                    name="descricao"
                    value={formData.descricao}
                    onChange={handleChange}
                    rows="3"
                    required
                    className={cn(errors.descricao && 'border-destructive focus-visible:ring-destructive')}
                />
                {errors.descricao && <p className="text-xs text-destructive">{errors.descricao}</p>}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="grid gap-2">
                    <Label htmlFor="dataInicio" className="mb-1">Data de Início:</Label> {/* Adicionado mb-1 */}
                    <DatePicker
                        date={formData.dataInicio}
                        setDate={(date) => handleDateChange('dataInicio', date)}
                        placeholder="Selecione a data de início"
                        disabled={isSubmitting}
                        className={cn(errors.dataInicio && 'border-destructive focus-visible:ring-destructive')}
                    />
                    {errors.dataInicio && <p className="text-xs text-destructive">{errors.dataInicio}</p>}
                </div>
                <div className="grid gap-2">
                    <Label htmlFor="horaInicio" className="mb-1">Hora de Início:</Label> {/* Adicionado mb-1 */}
                    <TimeInput
                        id="horaInicio"
                        name="horaInicio"
                        value={formData.horaInicio}
                        onChange={handleChange}
                        required
                        disabled={isSubmitting}
                        className={cn(errors.horaInicio && 'border-destructive focus-visible:ring-destructive')}
                    />
                    {errors.horaInicio && <p className="text-xs text-destructive">{errors.horaInicio}</p>}
                </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="grid gap-2">
                    <Label htmlFor="dataFim" className="mb-1">Data de Fim:</Label> {/* Adicionado mb-1 */}
                    <DatePicker
                        date={formData.dataFim}
                        setDate={(date) => handleDateChange('dataFim', date)}
                        placeholder="Selecione a data de fim"
                        disabled={isSubmitting}
                        className={cn(errors.dataFim && 'border-destructive focus-visible:ring-destructive')}
                    />
                    {errors.dataFim && <p className="text-xs text-destructive">{errors.dataFim}</p>}
                </div>
                <div className="grid gap-2">
                    <Label htmlFor="horaFim" className="mb-1">Hora de Fim:</Label> {/* Adicionado mb-1 */}
                    <TimeInput
                        id="horaFim"
                        name="horaFim"
                        value={formData.horaFim}
                        onChange={handleChange}
                        required
                        disabled={isSubmitting}
                        className={cn(errors.horaFim && 'border-destructive focus-visible:ring-destructive')}
                    />
                    {errors.horaFim && <p className="text-xs text-destructive">{errors.horaFim}</p>}
                </div>
            </div>
            <div className="flex flex-col sm:flex-row justify-end gap-2 mt-4"> {/* Adicionado flex-col sm:flex-row e w-full */}
                <Button type="button" variant="secondary" onClick={onCancel} disabled={isSubmitting} className="w-full sm:w-auto">
                    Cancelar
                </Button>
                <Button type="submit" disabled={isSubmitting} className="w-full sm:w-auto">
                    {isSubmitting ? 'A guardar...' : (sessao ? 'Atualizar Sessão' : 'Criar Sessão')}
                </Button>
            </div>
        </form>
    );
}

export default SessaoForm;